import { Fragment } from 'react'
import { Dialog, Transition } from '@headlessui/react'
import { XMarkIcon, ArrowsPointingOutIcon } from '@heroicons/react/24/outline'
import { ImageData } from '../types'

interface ImageModalProps {
  image: ImageData
  onClose: () => void
}

const ImageModal = ({ image, onClose }: ImageModalProps) => {
  const truncateImageId = (imageId: string) => {
    const parts = imageId.split('/')
    return parts[parts.length - 1]
  }

  const openInNewTab = (url: string) => {
    window.open(url, '_blank')
  }

  return (
    <Transition appear show={true} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-90" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-7xl transform overflow-hidden rounded-2xl bg-white dark:bg-gray-800 p-6 text-left align-middle shadow-xl transition-all">
                {/* Header */}
                <div className="flex items-center justify-between mb-6">
                  <Dialog.Title
                    as="h3"
                    className="text-xl font-semibold leading-6 text-gray-900 dark:text-white"
                  >
                    {truncateImageId(image.image_id)}
                    {image.division && (
                      <span className="ml-3 text-sm font-normal text-gray-500 dark:text-gray-400">
                        Division: {image.division}
                      </span>
                    )}
                  </Dialog.Title>
                  <button
                    type="button"
                    className="rounded-md text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 p-2"
                    onClick={onClose}
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>

                {/* Full Image ID */}
                <div className="mb-4 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                  <span className="text-xs text-gray-500 dark:text-gray-400">Full Image ID:</span>
                  <p className="text-sm text-gray-900 dark:text-white font-mono break-all mt-1">
                    {image.image_id}
                  </p>
                </div>

                {/* Images Grid - Three columns when Base Image 2 exists, otherwise two */}
                <div className={`grid gap-6 ${image.base_image_2_url ? 'grid-cols-1 lg:grid-cols-3' : 'grid-cols-1 lg:grid-cols-2'}`}>
                  {/* Base Image 1 */}
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <h4 className="text-lg font-medium text-gray-700 dark:text-gray-300">
                        Base Image 1
                      </h4>
                      <button
                        onClick={() => openInNewTab(image.base_image_url)}
                        className="flex items-center text-sm text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300"
                        title="Open in new tab"
                      >
                        <ArrowsPointingOutIcon className="w-4 h-4 mr-1" />
                        Full Size
                      </button>
                    </div>
                    <div className="aspect-square max-h-96 overflow-hidden rounded-xl bg-gray-100 dark:bg-gray-700 border-2 border-gray-200 dark:border-gray-600">
                      <img
                        src={image.base_image_url}
                        alt="Base image 1"
                        className="w-full h-full object-contain cursor-pointer hover:scale-105 transition-transform duration-200"
                        onClick={() => openInNewTab(image.base_image_url)}
                        onError={(e) => {
                          const img = e.target as HTMLImageElement
                          img.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgZmlsbD0iI2Y3ZjdmNyIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBkb21pbmFudC1iYXNlbGluZT0iY2VudHJhbCIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZmlsbD0iIzk5OTk5OSI+SW1hZ2UgTm90IEZvdW5kPC90ZXh0Pjwvc3ZnPg=='
                        }}
                      />
                    </div>
                  </div>

                  {/* Base Image 2 - Only show if it exists */}
                  {image.base_image_2_url && (
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <h4 className="text-lg font-medium text-gray-700 dark:text-gray-300">
                          Base Image 2
                        </h4>
                        <button
                          onClick={() => openInNewTab(image.base_image_2_url!)}
                          className="flex items-center text-sm text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300"
                          title="Open in new tab"
                        >
                          <ArrowsPointingOutIcon className="w-4 h-4 mr-1" />
                          Full Size
                        </button>
                      </div>
                      <div className="aspect-square max-h-96 overflow-hidden rounded-xl bg-gray-100 dark:bg-gray-700 border-2 border-gray-200 dark:border-gray-600">
                        <img
                          src={image.base_image_2_url}
                          alt="Base image 2"
                          className="w-full h-full object-contain cursor-pointer hover:scale-105 transition-transform duration-200"
                          onClick={() => openInNewTab(image.base_image_2_url!)}
                          onError={(e) => {
                            const img = e.target as HTMLImageElement
                            img.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgZmlsbD0iI2Y3ZjdmNyIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBkb21pbmFudC1iYXNlbGluZT0iY2VudHJhbCIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZmlsbD0iIzk5OTk5OSI+SW1hZ2UgTm90IEZvdW5kPC90ZXh0Pjwvc3ZnPg=='
                          }}
                        />
                      </div>
                    </div>
                  )}

                  {/* Result Image */}
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <h4 className="text-lg font-medium text-gray-700 dark:text-gray-300">
                        Result Image
                      </h4>
                      <button
                        onClick={() => openInNewTab(image.result_image_url)}
                        className="flex items-center text-sm text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300"
                        title="Open in new tab"
                      >
                        <ArrowsPointingOutIcon className="w-4 h-4 mr-1" />
                        Full Size
                      </button>
                    </div>
                    <div className="aspect-square max-h-96 overflow-hidden rounded-xl bg-gray-100 dark:bg-gray-700 border-2 border-gray-200 dark:border-gray-600">
                      <img
                        src={image.result_image_url}
                        alt="Result image"
                        className="w-full h-full object-contain cursor-pointer hover:scale-105 transition-transform duration-200"
                        onClick={() => openInNewTab(image.result_image_url)}
                        onError={(e) => {
                          const img = e.target as HTMLImageElement
                          img.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgZmlsbD0iI2Y3ZjdmNyIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBkb21pbmFudC1iYXNlbGluZT0iY2VudHJhbCIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZmlsbD0iIzk5OTk5OSI+SW1hZ2UgTm90IEZvdW5kPC90ZXh0Pjwvc3ZnPg=='
                        }}
                      />
                    </div>
                  </div>
                </div>

                {/* Annotation Details */}
                {(image.issues?.length || image.annotator_id) && (
                  <div className="mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
                    <h4 className="text-lg font-medium text-gray-700 dark:text-gray-300 mb-4">
                      Annotation Details
                    </h4>
                    <div className="space-y-4">
                      {image.issues && image.issues.length > 0 && (
                        <div>
                          <span className="text-sm font-medium text-gray-500 dark:text-gray-400">Issues:</span>
                          <div className="mt-2 flex flex-wrap gap-2">
                            {image.issues.map((issue, index) => (
                              <span
                                key={index}
                                className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200"
                              >
                                {issue}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                      {image.annotator_id && (
                        <div className="text-sm text-gray-500 dark:text-gray-400">
                          <span className="font-medium">Annotated by:</span> {image.annotator_id}
                          {image.annotation_updated_at && (
                            <span className="ml-4">
                              <span className="font-medium">Date:</span> {new Date(image.annotation_updated_at).toLocaleDateString()}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Footer */}
                <div className="mt-8 flex justify-between items-center">
                  <div className="text-sm text-gray-500 dark:text-gray-400">
                    Click images to open in full size in new tab
                  </div>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={onClose}
                  >
                    Close
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  )
}

export default ImageModal 